import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const STORAGE_PREFIX = "orgStats";

const buildStorageKey = (key, orgId) => `${STORAGE_PREFIX}:${orgId || "none"}:${key}`;

const readCache = (key, orgId) => {
  try {
    const raw = localStorage.getItem(buildStorageKey(key, orgId));
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};

const writeCache = (key, orgId, value) => {
  try {
    localStorage.setItem(buildStorageKey(key, orgId), JSON.stringify(value));
  } catch (_e) {
    // ignore (private browsing / storage quota) - the cache is a paint
    // convenience only, RTK Query's own in-memory cache is the source of
    // truth for anything this session actually needs.
  }
};

/**
 * Wraps an RTK Query stat-card query with localStorage persistence, so a
 * dashboard's numbers paint instantly from the last known value on mount
 * (a fresh page load has nothing in RTK Query's in-memory cache yet) instead
 * of every navigation re-showing a loading skeleton for numbers that
 * probably haven't changed. The network request still always fires - this
 * only changes what's on screen *while* it's in flight - and localStorage is
 * only rewritten when the fetched value actually differs from what's
 * cached, so a page that's re-rendering for an unrelated reason doesn't
 * churn storage writes for identical data.
 *
 * `refresh` exposes the underlying query's refetch for a manual "refresh
 * numbers" control - RTK Query's own refetch always hits the network
 * (bypassing its cache-hit dedupe), which is exactly what a manual refresh
 * button should do.
 *
 * Scoped per organisation. The wrapped query hook takes no arguments, so
 * switching orgs doesn't change RTK Query's cache key at all - without this,
 * both RTK Query's own `data` and this hook's localStorage cache would keep
 * showing the PREVIOUS org's numbers under the new org's name until
 * something else happened to trigger a refetch. On every `selectedOrg`
 * change this hook re-reads (or clears, if that org has never been fetched)
 * the new org's own cache slot and forces a real network refetch; `data` is
 * ignored via `dataOrgRef` until it can confirm the response actually
 * belongs to the currently selected org, not one already switched away from
 * by the time it arrived.
 *
 * @param {Function} useQueryHook - an RTK Query `use...Query` hook
 * @param {string} storageKey - unique key for this stat set, e.g. "company"
 * @param {*} [queryArgs] - optional args to pass through to useQueryHook (e.g.
 *   RevenuePage's `{ period }`). Different args get their own cache slot, both
 *   in RTK Query itself and in this hook's localStorage layer, so switching
 *   args doesn't flash the previous args' stale numbers.
 */
export const useOrgStats = (useQueryHook, storageKey, queryArgs) => {
  const selectedOrg = useSelector((state) => state?.auth?.user?.selectedOrg);
  const argsKey = queryArgs !== undefined ? JSON.stringify(queryArgs) : "";
  const effectiveKey = argsKey ? `${storageKey}:${argsKey}` : storageKey;
  const [cached, setCached] = useState(() => readCache(effectiveKey, selectedOrg));
  const { data, isFetching, isLoading, refetch } = useQueryHook(queryArgs);

  const lastOrgRef = useRef(selectedOrg);
  const lastKeyRef = useRef(effectiveKey);
  const dataOrgRef = useRef(selectedOrg);
  const dataKeyRef = useRef(effectiveKey);

  useEffect(() => {
    if (lastOrgRef.current === selectedOrg) return;
    lastOrgRef.current = selectedOrg;
    setCached(readCache(effectiveKey, selectedOrg));
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, effectiveKey]);

  // queryArgs changing (e.g. RevenuePage's period tab) - useQueryHook already
  // refetches on its own for new args, this just repaints from that args'
  // own cache slot instead of continuing to show the previous args' numbers.
  useEffect(() => {
    if (lastKeyRef.current === effectiveKey) return;
    lastKeyRef.current = effectiveKey;
    setCached(readCache(effectiveKey, selectedOrg));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveKey]);

  useEffect(() => {
    if (!data) return;
    dataOrgRef.current = selectedOrg;
    dataKeyRef.current = effectiveKey;
    setCached((prev) => {
      const nextJson = JSON.stringify(data);
      if (prev && JSON.stringify(prev) === nextJson) return prev;
      writeCache(effectiveKey, selectedOrg, data);
      return data;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, effectiveKey, selectedOrg]);

  // `data` may still be the previous org/args' RTK-Query-cached response for
  // a render or two after selectedOrg/queryArgs changes - don't hand it back
  // until both refs confirm it was actually fetched for what's selected now.
  const freshData = dataOrgRef.current === selectedOrg && dataKeyRef.current === effectiveKey ? data : undefined;

  return {
    stats: freshData || cached,
    // Only the very first, no-cache-to-fall-back-on load should show a
    // skeleton - a background refetch (isFetching but not isLoading, or any
    // fetch once `cached` is already populated) keeps showing the last good
    // numbers instead of blanking them out.
    isLoading: isLoading && !cached,
    isRefreshing: isFetching,
    refresh: refetch,
  };
};
